import React, { useEffect, useState } from 'react';
import { Briefcase, Car, Package, Utensils } from 'lucide-react';
import { Link } from 'react-router-dom';

import { BACKEND_URL } from '../../context/AuthContext';
import { HOME } from '../../constants/testIds';
import { serializeCapabilities } from '../admin/visual-canvas/canvasCapabilities';
import { normalizeLandingContent, resolveLandingAction } from './landingContent';
import './LandingRenderer.css';

const assetUrl = (value) => {
  const url = String(value || '').trim();
  if (!url) return '';
  if (url.startsWith('/uploads/') && BACKEND_URL) return `${BACKEND_URL}${url}`;
  return url;
};

const zoneForNode = (id) => id?.startsWith('service:') ? 'services'
  : id?.startsWith('benefit:') ? 'benefits'
    : id?.startsWith('provider') ? 'providers'
      : id?.split('.')[0] || 'landing';

const layoutStyle = (override = {}) => ({
  transform: override.offset_x !== undefined || override.offset_y !== undefined
    ? `translate(${Number(override.offset_x || 0)}px, ${Number(override.offset_y || 0)}px)`
    : undefined,
  width: override.image_frame_width ? `${override.image_frame_width}px` : override.width_percent ? `${override.width_percent}%` : undefined,
  height: override.image_frame_height ? `${override.image_frame_height}px` : undefined,
  minHeight: override.min_height ?? undefined,
  paddingTop: override.padding_top ?? undefined,
  paddingBottom: override.padding_bottom ?? undefined,
  minWidth: override.grid_span ? 0 : undefined,
  maxWidth: override.grid_span ? '100%' : override.max_width ?? undefined,
  textAlign: override.align || undefined,
  order: override.order ?? undefined,
  gridColumn: override.grid_span
    ? (override.grid_start ? `${override.grid_start} / span ${override.grid_span}` : `span ${override.grid_span} / span ${override.grid_span}`)
    : undefined,
});

const Selectable = ({ id, label, editable, directManipulation, selectedElement, onSelectElement, layout, zone, zoneRoot = false, as: Component = 'div', children, className = '', style }) => (
  <Component
    className={`${className} ${editable ? 'provi-landing-selectable' : ''} ${selectedElement === id ? 'is-selected' : ''}`.trim()}
    data-landing-element={id}
    data-landing-label={label}
    data-editor-node={id}
    data-editor-label={label}
    data-editor-zone={zone || zoneForNode(id)}
    data-editor-zone-root={zoneRoot ? (zone || zoneForNode(id)) : undefined}
    data-editor-capabilities={serializeCapabilities(id)}
    style={{ ...style, ...layoutStyle(layout) }}
    onClick={editable && !directManipulation ? (event) => {
      event.preventDefault();
      event.stopPropagation();
      onSelectElement?.(id);
    } : undefined}
  >
    {children}
  </Component>
);

const ActionLink = ({ action, url, editable, className = '', children, testId, style }) => {
  const target = resolveLandingAction(action, url);
  if (editable) {
    return (
      <button
        type="button"
        className={className}
        style={style}
        data-testid={testId}
        data-editable-action={action || 'missing'}
        data-action-unavailable={target.available ? undefined : action || 'missing'}
      >
        {children}{!target.available && <small className="provi-action-unavailable">Acción no disponible</small>}
      </button>
    );
  }
  if (!target.available) {
    return (
      <button type="button" disabled className={className} style={style} title="Acción no disponible" data-action-unavailable={action || 'missing'}>
        {children}{editable && <small className="provi-action-unavailable">Acción no disponible</small>}
      </button>
    );
  }
  if (target.external || target.href.startsWith('#')) {
    return <a href={target.href} target={target.external ? '_blank' : undefined} rel={target.external ? 'noopener noreferrer' : undefined} className={className} style={style} data-testid={testId}>{children}</a>;
  }
  return <Link to={target.href} className={className} style={style} data-testid={testId}>{children}</Link>;
};

const NavigationLink = ({ href, editable, children }) => editable
  ? <button type="button" data-navigation-href={href} style={{ background: 'none', border: 0, color: 'inherit', font: 'inherit', padding: 0 }}>{children}</button>
  : <a href={href} data-navigation-href={href}>{children}</a>;

const ServiceIcon = ({ type }) => {
  if (type === 'square') return <div className="size-4 rounded-sm bg-black/80" />;
  if (type === 'ring') return <div className="size-4 rounded-full border-2 border-black/80" />;
  return <div className="size-4 rounded-full bg-black/80" />;
};

const LandingRenderer = ({ content, mode = 'responsive', editable = false, directManipulation = false, selectedElement = null, onSelectElement }) => {
  const data = normalizeLandingContent(content);
  const [responsiveMobile, setResponsiveMobile] = useState(() => mode === 'responsive' && typeof window !== 'undefined' && window.matchMedia?.('(max-width: 767px)').matches);
  useEffect(() => {
    if (mode !== 'responsive' || typeof window === 'undefined' || !window.matchMedia) return undefined;
    const media = window.matchMedia('(max-width: 767px)');
    const update = () => setResponsiveMobile(media.matches);
    update();
    if (media.addEventListener) media.addEventListener('change', update);
    else media.addListener?.(update);
    window.addEventListener('orientationchange', update);
    return () => {
      if (media.removeEventListener) media.removeEventListener('change', update);
      else media.removeListener?.(update);
      window.removeEventListener('orientationchange', update);
    };
  }, [mode]);
  const mobile = mode === 'mobile' || (mode === 'responsive' && responsiveMobile);
  const device = mobile ? 'mobile' : 'desktop';
  const editorLayout = data.editor_layout?.[device] || {};
  const isVisible = (id) => data.element_visibility?.[id] !== false;
  const spacing = data.layout_spacing[mobile ? 'mobile' : 'desktop'];
  const imageForMode = (image) => assetUrl(mobile && image.mobile_url ? image.mobile_url : image.desktop_url);
  const selectable = { editable, directManipulation, selectedElement, onSelectElement };
  const node = (id, zone) => ({
    ...selectable,
    layout: id?.startsWith('service:')
      ? { ...editorLayout[id], grid_start: mobile ? undefined : editorLayout[id]?.grid_start, grid_span: mobile ? 12 : Number(editorLayout[id]?.grid_span || 4) }
      : editorLayout[id],
    zone,
  });
  const primaryCta = (
    <Selectable id="hero.primary_cta" label="CTA del Hero" {...node('hero.primary_cta', 'hero')} className="flex shrink-0">
      <ActionLink {...data.hero.primary_cta} editable={editable} className="flex w-full items-center justify-center rounded-lg bg-white px-6 py-2.5 text-sm font-bold text-black transition-all hover:bg-[#00F0FF]">{data.hero.primary_cta.text}</ActionLink>
    </Selectable>
  );

  const hero = data.hero.visible ? (
    <Selectable key="hero" id="hero" label="Hero" {...node('hero')} zoneRoot as="header" className="provi-landing-hero relative overflow-hidden text-center" style={{
      backgroundColor: data.hero.background_color,
      marginBottom: spacing.hero_services_gap
    }}>
      {isVisible('hero.image') && imageForMode(data.hero.image) && <Selectable id="hero.image" label="Imagen del Hero" {...node('hero.image', 'hero')} className="provi-landing-hero-image-frame"><img src={imageForMode(data.hero.image)} alt="" className="provi-landing-hero-image" style={{
        objectFit: data.hero.image.fit,
        objectPosition: `${data.hero.image.position_x}% ${data.hero.image.position_y}%`,
        transform: `scale(${data.hero.image.scale})`,
        borderRadius: data.hero.image.radius
      }} /></Selectable>}
      <div className="absolute left-1/2 top-0 h-[500px] w-full -translate-x-1/2 rounded-full bg-[#FF007F]/10 blur-[120px]" />
      <div className="absolute right-[-10%] top-24 h-72 w-72 rounded-full bg-[#00F0FF]/10 blur-[100px]" />
      <div className="relative z-10 mx-auto max-w-5xl">
        <Selectable id="hero.title" label="Título del Hero" {...node('hero.title', 'hero')} as="h1" className="mb-6 font-display text-5xl font-bold tracking-tighter md:text-7xl">
          {data.hero.title}<br />
          <span className="bg-gradient-to-r from-[#FF007F] via-white to-[#00F0FF] bg-clip-text text-transparent">{data.hero.highlighted_text}</span>
        </Selectable>
        {isVisible('hero.subtitle') && <Selectable id="hero.subtitle" label="Subtítulo del Hero" {...node('hero.subtitle', 'hero')} as="p" className="mx-auto mb-10 max-w-2xl text-lg text-white/60 md:text-xl">
          {data.hero.subtitle}
        </Selectable>}
        {isVisible('hero.search') && <Selectable id="hero.search" label="Buscador principal" {...node('hero.search', 'hero')} className="mx-auto max-w-2xl rounded-xl border border-white/10 bg-[#111217] p-1.5 shadow-2xl shadow-[#00F0FF]/5">
          <div className="flex flex-col gap-1.5 md:flex-row md:items-stretch">
            <div className="flex-1 rounded-lg px-4 py-2.5 text-left"><span className="mb-0.5 block text-[9px] font-bold uppercase tracking-widest text-[#00F0FF]">¿Qué necesitas?</span><span className="text-sm font-medium italic">{data.hero.service_prompt}</span></div>
            <div className="hidden w-px bg-white/10 md:block" />
            <div className="flex-1 rounded-lg px-4 py-2.5 text-left"><span className="mb-0.5 block text-[9px] font-bold uppercase tracking-widest text-[#FF007F]">Ubicación</span><span className="text-sm font-medium">{data.hero.location_text}</span></div>
            {primaryCta}
          </div>
        </Selectable>}
        {!isVisible('hero.search') && (
          <div className="mx-auto flex max-w-2xl justify-center">
            {primaryCta}
          </div>
        )}
      </div>
    </Selectable>
  ) : null;

  const services = data.services.visible ? (
    <Selectable key="services" id="services" label="Servicios" {...node('services')} zoneRoot as="section" className="provi-landing-services" style={{
      backgroundColor: data.services.background_color,
    }}>
      <div id="servicios" className="mx-auto grid max-w-6xl grid-cols-12" style={{ gap: spacing.services_gap }}>
        {data.services.cards.filter((service) => service.visible).map((service) => (
          <Selectable key={service.id} id={`service:${service.id}`} label={`Servicio · ${service.title}`} {...node(`service:${service.id}`, 'services')} className="relative" style={{ minWidth: 0, maxWidth: '100%' }}>
            <ActionLink action={service.action} url={service.url} editable={editable} className="group relative block h-full overflow-hidden border transition-all hover:shadow-lg" style={{
              background: `linear-gradient(135deg, ${service.accent_color}22, ${service.background_color} 55%)`,
              borderColor: `${service.border_color}33`, borderRadius: service.radius, padding: service.padding
            }}>
              {assetUrl(service.image_url) ? <div className="provi-service-image-wrap"><img src={assetUrl(service.image_url)} alt="" style={{ objectFit: service.image_fit, objectPosition: `${service.image_position_x}% ${service.image_position_y}%`, transform: `scale(${service.image_scale})` }} /></div> : (
                <div className="relative mb-4 flex size-11 items-center justify-center rounded-xl shadow-md" style={{ background: `linear-gradient(135deg, ${service.accent_color}, ${service.accent_color}66)` }}><ServiceIcon type={service.icon} /></div>
              )}
              <h3 className="mb-1.5 font-display text-xl font-bold">{service.title}</h3>
              <p className="mb-4 text-xs leading-relaxed text-white/60">{service.description}</p>
              <span className="flex items-center gap-1.5 text-sm font-bold" style={{ color: service.accent_color }}>{service.button_text}</span>
            </ActionLink>
          </Selectable>
        ))}
      </div>
    </Selectable>
  ) : null;

  const benefits = data.benefits.visible ? (
    <Selectable key="benefits" id="benefits" label="Beneficios" {...node('benefits')} zoneRoot as="section" className="provi-landing-benefits border-y border-white/5" style={{ backgroundColor: data.benefits.background_color, marginTop: spacing.benefits_margin }}>
      <div className="mx-auto grid max-w-6xl grid-cols-2 gap-8 md:grid-cols-4">
        {data.benefits.items.filter((item) => item.visible).map((benefit) => (
          <Selectable key={benefit.id} id={`benefit:${benefit.id}`} label={`Beneficio · ${benefit.label}`} {...node(`benefit:${benefit.id}`, 'benefits')} className="flex flex-col items-center text-center">
            <span className="mb-2 font-display text-xs font-bold uppercase tracking-widest" style={{ color: benefit.color }}>{benefit.label}</span>
            <p className="text-sm text-white/70">{benefit.value}</p>
          </Selectable>
        ))}
      </div>
    </Selectable>
  ) : null;

  const providers = data.providers.visible ? (
    <Selectable key="providers" id="providers" label="Proveedores" {...node('providers')} zoneRoot as="section" className="provi-landing-providers" style={{ marginTop: spacing.providers_margin }}>
      <div id="proveedores" className="relative mx-auto max-w-5xl overflow-hidden border border-white/10 p-8 md:rounded-[40px] md:p-12" style={{ background: `linear-gradient(135deg, ${data.providers.background_color}33, #00F0FF33)` }}>
        <div className="relative z-10 flex flex-col items-center justify-between gap-12 md:flex-row">
          <div className="max-w-md text-center md:text-left">
            {isVisible('providers.title') && <Selectable id="providers.title" label="Título de proveedores" {...node('providers.title', 'providers')} as="h2" className="mb-6 font-display text-4xl font-bold">{data.providers.title}</Selectable>}
            {isVisible('providers.description') && <Selectable id="providers.description" label="Descripción de proveedores" {...node('providers.description', 'providers')} as="p" className="mb-8 text-white/70">{data.providers.description}</Selectable>}
            {isVisible('providers.actions') && <Selectable id="providers.actions" label="Botones de proveedores" {...node('providers.actions', 'providers')} className="flex flex-wrap items-center justify-center gap-4 md:justify-start">
              <ActionLink action={data.providers.register_action} editable={editable} testId="provider-register-button" className="rounded-2xl bg-white px-8 py-4 font-bold text-black hover:bg-[#00F0FF]">{data.providers.register_text}</ActionLink>
              <ActionLink action={data.providers.login_action} editable={editable} className="rounded-2xl border border-[#00F0FF]/50 bg-[#00F0FF]/10 px-8 py-4 font-bold text-[#00F0FF]">{data.providers.login_text}</ActionLink>
              <ActionLink action={data.providers.requirements_action} editable={editable} className="rounded-2xl border border-white/20 px-8 py-4 font-bold">{data.providers.requirements_text}</ActionLink>
            </Selectable>}
          </div>
          <div className="hidden md:block">
            {isVisible('providers.image') && (imageForMode(data.providers.image) ? <Selectable id="providers.image" label="Imagen de proveedores" {...node('providers.image', 'providers')} className="provi-landing-provider-image-frame"><img src={imageForMode(data.providers.image)} alt="" style={{ objectFit: data.providers.image.fit, objectPosition: `${data.providers.image.position_x}% ${data.providers.image.position_y}%`, transform: `scale(${data.providers.image.scale})`, borderRadius: data.providers.image.radius }} /></Selectable> : (
              <div className="relative grid size-64 grid-cols-2 place-content-center gap-4 rounded-3xl border border-white/10 bg-black/25 p-8">
                {[Car, Package, Utensils, Briefcase].map((Icon, index) => <div key={index} className="flex size-20 items-center justify-center rounded-2xl border border-white/10 bg-black/40"><Icon className="size-9 text-[#00F0FF]" strokeWidth={1.5} /></div>)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Selectable>
  ) : null;

  const sectionMap = { hero, services, benefits, providers };

  return (
    <div className={`provi-landing-renderer landing-mode-${mode} min-h-screen bg-[#07080A] font-sans text-white selection:bg-[#00F0FF]/30`} data-testid="landing-renderer" data-editable={editable ? 'true' : 'false'} style={{
      '--landing-mobile-x': `${data.layout_spacing.mobile.section_padding_x}px`,
      '--landing-mobile-y': `${data.layout_spacing.mobile.section_padding_y}px`,
      '--landing-desktop-x': `${data.layout_spacing.desktop.section_padding_x}px`,
      '--landing-desktop-y': `${data.layout_spacing.desktop.section_padding_y}px`
    }}>
      {data.navigation.visible && (
        <Selectable id="navigation" label="Navegación" {...node('navigation')} zoneRoot as="nav" className="flex items-center justify-between border-b border-white/5" style={{ backgroundColor: data.navigation.background_color, padding: `${mobile ? data.navigation.spacing.mobile.vertical : data.navigation.spacing.desktop.vertical}px ${mobile ? data.navigation.spacing.mobile.horizontal : data.navigation.spacing.desktop.horizontal}px` }}>
          <Selectable id="navigation.brand" label="Marca" {...node('navigation.brand', 'navigation')} className="flex items-center gap-2">
            {assetUrl(data.navigation.logo_url) ? <img src={assetUrl(data.navigation.logo_url)} alt="" className="size-8 rounded-lg object-contain" /> : <div className="size-8 rounded-lg bg-gradient-to-br from-[#FF007F] to-[#00F0FF]" />}
            <span className="font-display text-xl font-bold italic tracking-tight">{data.navigation.brand_name}</span>
          </Selectable>
          <div className="hidden gap-8 text-sm font-medium text-white/60 md:flex">
            <NavigationLink href="#servicios" editable={editable}>Servicios</NavigationLink>
            <NavigationLink href="#proveedores" editable={editable}>Proveedores</NavigationLink>
            <NavigationLink href="#soporte" editable={editable}>Soporte</NavigationLink>
          </div>
          <div className="flex items-center gap-3">
            <Selectable id="navigation.login" label="Login" {...node('navigation.login', 'navigation')}><ActionLink action="go_login" editable={editable} testId={HOME.loginLink} className="rounded-full border border-[#00F0FF]/50 bg-[#00F0FF]/10 px-4 py-2 text-xs font-bold text-[#00F0FF] sm:px-6 sm:py-2.5 sm:text-sm">{data.navigation.login_text}</ActionLink></Selectable>
            <Selectable id="navigation.register" label="Registro" {...node('navigation.register', 'navigation')}><ActionLink action="go_register_client" editable={editable} testId={HOME.getStartedButton} className="rounded-full bg-white px-4 py-2 text-xs font-medium text-black sm:px-5 sm:text-sm">{data.navigation.register_text}</ActionLink></Selectable>
          </div>
        </Selectable>
      )}
      <main>{data.section_order.map((section) => sectionMap[section])}</main>
      {data.footer.visible && (
        <Selectable id="footer" label="Footer" {...node('footer')} zoneRoot as="footer" className="border-t border-white/5 px-6 py-12 text-center" style={{ backgroundColor: data.footer.background_color }}>
          <p className="text-xs uppercase tracking-widest text-white/30">{data.footer.legal_text}</p>
          <div className="mt-4 flex flex-wrap justify-center gap-6 text-xs font-medium text-white/50">
            <ActionLink action={data.footer.terms_action} url={data.footer.terms_url} editable={editable}>{data.footer.terms_text}</ActionLink>
            <ActionLink action={data.footer.privacy_action} url={data.footer.privacy_url} editable={editable}>{data.footer.privacy_text}</ActionLink>
            <ActionLink action={data.footer.support_action} url={data.footer.support_url} editable={editable}>{data.footer.support_text}</ActionLink>
          </div>
        </Selectable>
      )}
    </div>
  );
};

export default LandingRenderer;
